import React from 'react'
import { connect } from 'react-redux'
import { isNil } from 'lodash'

class WindOverlay3D extends React.Component {
  constructor(props) {
    super(props)
    this.containerDiv = null
    this.canvas = null
    this.frameRequest = 0
    this.animateCanvas = this.animateCanvas.bind(this)
    this.updatePosition = this.updatePosition.bind(this)
    this.particleConfig = {
      velocity: 2,
      radius: 10,
      r: 255,
      g: 255,
      b: 255,
      maxParticleAge: 10000
    }
  }

  componentDidMount() {
    const { mapReferenceThreed, position } = this.props
    if (!mapReferenceThreed || !position?.lat || !position?.lng) return
    this.createOverlay()
  }

  getVisualRotation() {
    const { mapReferenceThreed, direction } = this.props
    const bearing = (mapReferenceThreed && typeof mapReferenceThreed.getBearing === 'function')
      ? mapReferenceThreed.getBearing()
      : 0
    return direction - bearing
  }

  updateRotation() {
    if (this.containerDiv) {
      this.containerDiv.style.transform = `rotate(${this.getVisualRotation()}deg)`
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.direction !== this.props.direction && this.containerDiv) {
      this.updateRotation()
    }
    if (
      (prevProps.position?.lat !== this.props.position?.lat ||
       prevProps.position?.lng !== this.props.position?.lng) &&
      this.containerDiv
    ) {
      this.updatePosition()
    }
  }

  componentWillUnmount() {
    const { mapReferenceThreed } = this.props
    cancelAnimationFrame(this.frameRequest)
    if (mapReferenceThreed) {
      mapReferenceThreed.off('move', this.updatePosition)
      mapReferenceThreed.off('rotate', this.updateRotation)
    }
    if (this.containerDiv) {
      this.containerDiv.remove()
      this.containerDiv = null
    }
  }

  createOverlay() {
    const { mapReferenceThreed, direction, hideArrow } = this.props
    const mapContainer = mapReferenceThreed.getContainer()

    // Container sized to ~400px square, centered on the clicked point.
    // transform-origin: center so rotation pivots around the click point.
    const div = document.createElement('div')
    div.style.cssText = [
      'position:absolute',
      'width:400px',
      'height:400px',
      'pointer-events:none',
      'transform-origin:center center',
    ].join(';')
    div.style.transform = `rotate(${this.getVisualRotation()}deg)`

    // Canvas mirrors the 2D CSS: top:50% left:50% width:50% height:39%
    // rotated 90deg with blur so particles appear to flow in the wind direction.
    const canvas = document.createElement('canvas')
    canvas.width = 500
    canvas.height = 1000
    canvas.style.cssText = [
      'position:absolute',
      'width:50%',
      'height:39%',
      'top:50%',
      'left:50%',
      'transform:rotate(90deg) translateY(-50%)',
      'transform-origin:left top',
      'filter:blur(8px)',
    ].join(';')

    const arrow = document.createElement('img')
    arrow.src = '/asset/wind-overlay-arrow.png'
    arrow.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'height:20px',
      'transform:translate(-50%,-50%)',
    ].join(';')
    if (hideArrow) arrow.style.visibility = 'hidden'

    div.appendChild(canvas)
    div.appendChild(arrow)
    mapContainer.appendChild(div)

    this.containerDiv = div
    this.canvas = canvas

    this.updatePosition()
    this.updateRotation = this.updateRotation.bind(this)
    mapReferenceThreed.on('move', this.updatePosition)
    mapReferenceThreed.on('rotate', this.updateRotation)

    this.frameRequest = requestAnimationFrame(time => this.animateCanvas(canvas, [], time))
  }

  updatePosition() {
    const { mapReferenceThreed, position } = this.props
    if (!this.containerDiv || !position?.lat || !position?.lng) return
    const point = mapReferenceThreed.project([position.lng, position.lat])
    this.containerDiv.style.left = (point.x - 200) + 'px'
    this.containerDiv.style.top = (point.y - 200) + 'px'
  }

  animateCanvas(canvas, particles = [], timeStamp) {
    const context = canvas.getContext('2d'),
      width = canvas.width,
      height = canvas.height,
      emitter = { position: { x: 0, y: canvas.height / 2 } },
      velocity = !isNaN(parseInt(this.props.speed)) ? this.props.speed / 10 : 0,
      particleConfig = this.particleConfig

    class Particle {
      constructor(x, y, velocityY, spawned) {
        this.radius = particleConfig.radius
        this.velocityY = velocityY
        this.position = { x, y }
        this.spawned = spawned
      }
      shouldRemove(time) {
        const dist = Math.sqrt(
          Math.pow(emitter.position.x - this.position.x, 2) +
          Math.pow(emitter.position.y - this.position.y, 2)
        )
        return (
          isNaN(this.spawned) ||
          time - this.spawned >= particleConfig.maxParticleAge ||
          dist >= width
        )
      }
      move(distX, distY = null) {
        this.position.x += !isNil(distX) ? distX : velocity
        this.position.y += !isNil(distY) ? distY : this.velocityY
      }
    }

    const steps = Math.floor(width / Math.max(velocity, 0.01)),
      maxVel = (height / Math.max(1, velocity)) / steps,
      randVelocity = (Math.random() * maxVel) - (maxVel / 2)

    particles = particles.filter(p => !p.shouldRemove(timeStamp)).concat([
      new Particle(emitter.position.x, emitter.position.y, randVelocity, timeStamp),
      new Particle(emitter.position.x, emitter.position.y, randVelocity, timeStamp),
    ])

    context.clearRect(0, 0, width, height)

    particles.forEach(particle => {
      particle.move(this.particleConfig.x)
      const { x, y } = particle.position,
        { radius, spawned } = particle,
        ageAlpha = 1 - ((timeStamp - spawned) / this.particleConfig.maxParticleAge),
        dist = Math.sqrt(
          Math.pow(emitter.position.x - x, 2) +
          Math.pow(emitter.position.y - y, 2)
        ),
        alpha = Math.min(ageAlpha, 1 - (dist / width)),
        { r, g, b } = this.particleConfig

      context.fillStyle = `rgba(${r},${g},${b},${alpha})`
      context.beginPath()
      context.arc(x, y, radius, 0, 2 * Math.PI)
      context.fill()
    })

    this.frameRequest = requestAnimationFrame(time => this.animateCanvas(canvas, particles, time))
  }

  render() {
    if (this.containerDiv) {
      this.updateRotation()
      this.updatePosition()
    }
    return null
  }
}

const mapStateToProps = state => ({
  mapReferenceThreed: state.mapReferenceThreed,
})

export default connect(mapStateToProps)(WindOverlay3D)
